import React, { useState, useRef, useEffect } from 'react';
import { Menu, X, Mail, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

const SERVICE_LINKS = [
  { label: 'IT Support & Maintenance', href: '/it-support' },
  { label: 'Cyber Security', href: '/cybersecurity' },
  { label: 'Microsoft 365', href: '/microsoft-365' },
  { label: '24/7 Server Monitoring', href: '/server-monitoring' },
  { label: 'Hardware & Software Supply', href: '/hardware-supply' },
  { label: 'VoIP & Telecoms', href: '/voip-telecoms' },
  { label: 'Websites & Apps', href: '/websites-apps' },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const scrollToSection = (sectionId) => {
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    } else {
      navigate(`/#${sectionId}`);
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      {/* Top Contact Bar */}
      <div className="bg-slate-950 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
          <div className="flex justify-end items-center gap-6 text-sm text-slate-400">
            <a href="tel:01457855590" className="flex items-center gap-2 hover:text-blue-400 transition-colors">
              <Phone className="w-4 h-4" />
              <span>01457 855590</span>
            </a>
            <a href="mailto:helpme@bincon.co.uk" className="flex items-center gap-2 hover:text-blue-400 transition-colors">
              <Mail className="w-4 h-4" />
              <span>helpme@bincon.co.uk</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-md sticky top-0 z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <a href="/" className="flex items-center flex-shrink-0" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_7efe1680-f0b9-4551-bd7a-719bbe173f82/artifacts/ux0r9sdw_Binary_Logo_PNG_Black.png"
                alt="Binary Consultancy"
                className="h-16 sm:h-20 w-auto cursor-pointer"
              />
            </a>

            {/* Desktop Navigation — centred */}
            <nav className="hidden lg:flex items-center justify-center flex-1 space-x-6">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-slate-700 hover:text-blue-600 font-medium transition-colors text-sm">Home</button>
              <button onClick={() => { navigate('/about'); setMobileMenuOpen(false); }} className="text-slate-700 hover:text-blue-600 font-medium transition-colors text-sm">About Us</button>
              <button onClick={() => navigate('/remote-support')} className="text-slate-700 hover:text-blue-600 font-medium transition-colors text-sm">Support</button>

              {/* Services Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className="flex items-center gap-1 text-slate-700 hover:text-blue-600 font-medium transition-colors text-sm"
                  data-testid="services-dropdown-btn"
                >
                  Services <ChevronDown className={`w-3.5 h-3.5 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {servicesOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-xl py-2 z-50" data-testid="services-dropdown-menu">
                    <button
                      onClick={() => { scrollToSection('services'); setServicesOpen(false); }}
                      className="block w-full text-left px-4 py-2.5 text-sm text-blue-600 font-semibold hover:bg-slate-50 transition-colors"
                    >
                      All Services Overview
                    </button>
                    <div className="border-t border-slate-100 my-1" />
                    {SERVICE_LINKS.map((item) => (
                      <button
                        key={item.href}
                        onClick={() => { navigate(item.href); setServicesOpen(false); }}
                        className="block w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-blue-600 transition-colors"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button onClick={() => navigate('/case-studies')} className="text-slate-700 hover:text-blue-600 font-medium transition-colors text-sm">Case Studies</button>
              <button onClick={() => navigate('/blog')} className="text-slate-700 hover:text-blue-600 font-medium transition-colors text-sm">Blog</button>
              <button onClick={() => window.location.href = '/newsletter'} className="text-slate-700 hover:text-blue-600 font-medium transition-colors text-sm">Newsletter</button>
            </nav>

            <Button onClick={() => scrollToSection('contact')} className="hidden lg:flex bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 py-2.5 font-medium shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/40 transition-all flex-shrink-0">
              <Mail className="w-4 h-4 mr-2" /> Contact Us
            </Button>

            {/* Mobile Menu Button */}
            <button className="lg:hidden text-slate-700 hover:text-blue-600" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-slate-100 max-h-[70vh] overflow-y-auto">
            <div className="px-4 py-4 space-y-1">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors font-medium">Home</button>
              <button onClick={() => { navigate('/about'); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors font-medium">About Us</button>
              <button onClick={() => { navigate('/remote-support'); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors font-medium">Support</button>

              {/* Mobile Services Accordion */}
              <div>
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="flex items-center justify-between w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors font-medium"
                >
                  Services <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileServicesOpen && (
                  <div className="pl-4 space-y-0.5">
                    <button onClick={() => { scrollToSection('services'); setMobileMenuOpen(false); setMobileServicesOpen(false); }} className="block w-full text-left px-4 py-2.5 text-sm text-blue-600 font-semibold hover:bg-slate-50 rounded-lg">All Services</button>
                    {SERVICE_LINKS.map((item) => (
                      <button
                        key={item.href}
                        onClick={() => { navigate(item.href); setMobileMenuOpen(false); setMobileServicesOpen(false); }}
                        className="block w-full text-left px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 rounded-lg"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button onClick={() => { navigate('/case-studies'); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors font-medium">Case Studies</button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors font-medium">Blog</button>
              <button onClick={() => { window.location.href = '/newsletter'; setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors font-medium">Newsletter</button>
              <Button onClick={() => scrollToSection('contact')} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium">
                <Mail className="w-4 h-4 mr-2" /> Contact Us
              </Button>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
